import { replace, push } from '@lagunovsky/redux-react-router';

import { createLogicWithApi, createLogicWithRouteChecks } from '../../../../shared/logicCreators';
import { table as tableConfig } from '../../../config/box';
import { URL_CHANGE } from '../../../../shared/components/App/App.actions';
import {
  boxes as apiConfig,
  compartments as compartmentsApiConfig,
  locations as locationsApiConfig
} from '../../../config/api';
import {
  FETCH_BOXES,
  CANCEL_FETCH_BOXES,
  FETCH_BOX_BY_ID,
  CANCEL_FETCH_BOX_BY_ID,
  SAVE_BOX_BY_ID,
  CANCEL_SAVE_BOX_BY_ID,
  SAVE_BOX_BY_ID_SUCCESS,
  REMOVE_BOX_BY_ID,
  CANCEL_REMOVE_BOX_BY_ID,
  REMOVE_BOX_BY_ID_SUCCESS,
  selectBoxTable,
  fetchBoxById,
  fetchBoxesSuccess,
  fetchBoxesError,
  fetchBoxByIdSuccess,
  fetchBoxByIdError,
  saveBoxByIdSuccess,
  saveBoxByIdError,
  removeBoxByIdSuccess,
  removeBoxByIdError,
  FETCH_COMPARTMENTS,
  CANCEL_FETCH_COMPARTMENTS,
  FETCH_COMPARTMENT,
  CANCEL_FETCH_COMPARTMENT,
  SAVE_COMPARTMENT,
  CANCEL_SAVE_COMPARTMENT,
  SAVE_COMPARTMENT_SUCCESS,
  REMOVE_COMPARTMENT,
  CANCEL_REMOVE_COMPARTMENT,
  REMOVE_COMPARTMENT_SUCCESS,
  fetchCompartment,
  fetchCompartmentsSuccess,
  fetchCompartmentsError,
  fetchCompartmentSuccess,
  fetchCompartmentError,
  saveCompartmentSuccess,
  saveCompartmentError,
  removeCompartmentSuccess,
  removeCompartmentError,
  resetCompartmentSelected
} from '../../../../shared/components/Box/Box.actions';

const checkBoxRouteLogic = createLogicWithRouteChecks({
  type: URL_CHANGE,
  process: ({
    checkUnselectedIdParam,
    checkInitSelectWithLocation,
    getUrl,
    checkInitLocation,
    state
  }, dispatch, done) => {
    const locationId = checkInitLocation();

    if (locationId) {
      const selectId = checkInitSelectWithLocation('boxes');

      if (selectId) {
        dispatch(replace(`${getUrl('boxes')}/${locationId}/${selectId}${window.location.search}`));

      } else {
        const newIdParam = checkUnselectedIdParam('boxes', locationId);

        if (newIdParam !== false) {
          dispatch(selectBoxTable(newIdParam));
          dispatch(fetchBoxById({
            id: newIdParam,
            location: locationId
          }));
        }

        // Custom compartment check (same logic as checkUnselectedVariantParam but uses 'compartments' instead of 'variants')
        const reservedPathNames = ['EDIT', 'CREATE'];
        let compartmentId = false;

        if (state.login?.loggedIn && state.router.location.pathname.indexOf(state.locale.boxes.route.path) === 0) {
          const params = state.router.location.pathname.split('/');
          params.shift(); // remove empty first element
          params.shift(); // remove location

          if (!params.slice(0, 3).find(p => !!~reservedPathNames.indexOf(p.toUpperCase()))) {
            compartmentId = params[2] && !isNaN(params[2]) ? params[2] : false;
          }

          if (
            compartmentId &&
            (
              !state.boxes?.compartments?.selected?.id ||
              String(compartmentId) !== String(state.boxes.compartments.selected.id)
            )
          ) {
            dispatch(fetchCompartment({
              id: compartmentId,
              boxId: newIdParam
            }));
          } else if (!compartmentId && state.boxes?.compartments?.selected?.id) {
            // Reset compartment selection when URL no longer has compartment ID
            dispatch(resetCompartmentSelected());
          }
        }
      }
    }
    done();
  }
});

const fetchBoxesLogic = createLogicWithApi({
  type: FETCH_BOXES,
  cancelType: CANCEL_FETCH_BOXES,
  latest: true,
  process: ({ Api, getState, action }, dispatch, done) => {
    const state = getState(),
      location = action.location ?
        action.location :
        state.table.currentLocation || state.router.location.pathname.split('/')[2] || null;

    return Api({
      method: 'get',
      path: `${locationsApiConfig.path}/${location}${apiConfig.path}`,
      handle: r => {
        const state = getState();
        const sort = action.sort ? action.sort : state.table.items[tableConfig.name]?.sort;
        const queryFields = action.queryFields;
        const desc = typeof action.desc !== 'undefined' ? action.desc : state.table.items[tableConfig.name]?.desc;
        const filters = typeof action.filters !== 'undefined' ? action.filters : state.table.filters;
        const search = typeof action.search !== 'undefined' ? action.search : state.table.search;
        const limit = typeof action.limit !== 'undefined' ? action.limit : state.table.items[tableConfig.name]?.limit;
        const offset = typeof action.offset !== 'undefined' ? action.offset : state.table.items[tableConfig.name]?.offset;

        if (offset) {
          r.query({ 'offset': offset });
        }

        if (queryFields) {
          r.query({ 'queryFields': queryFields });
        }

        r
          .query({ 'limit': limit })
          .query({ 'filters': filters })
          .query({ 'query': search })
          .query({ 'order[field]': sort })
          .query({ 'order[dir]': desc ? 'desc' : 'asc' })
          .query({ 'filters[state]': 'published,temporary' });

        return r;
      }
    })
      .then(resp => {
        dispatch(fetchBoxesSuccess(resp));
        done();
      })
      .catch(err => {
        dispatch(fetchBoxesError(err));
        done();
      });
  }
});

const fetchBoxByIdLogic = createLogicWithApi({
  type: FETCH_BOX_BY_ID,
  cancelType: CANCEL_FETCH_BOX_BY_ID,
  latest: true,
  process: ({ Api, getState, action }, dispatch, done) => {
    const state = getState(),
      location = action.location ?
        action.location :
        state.table.currentLocation || state.router.location.pathname.split('/')[2] || null;

    return Api({
      method: 'get',
      path: `${locationsApiConfig.path}/${location}${apiConfig.path}/${action.id}`
    })
      .then(resp => {
        dispatch(fetchBoxByIdSuccess(resp));
        done();
      })
      .catch(err => {
        dispatch(fetchBoxByIdError(err));
        done();
      });
  }
});

const saveBoxByIdLogic = createLogicWithApi({
  type: SAVE_BOX_BY_ID,
  cancelType: CANCEL_SAVE_BOX_BY_ID,
  latest: true,
  process: ({ Api, getState, action }, dispatch, done) => {
    const state = getState(),
      location = state.table.currentLocation || state.router.location.pathname.split('/')[2] || null,
      data = action.payload,
      id = data?.id;

    return Api({
      method: id ? 'patch' : 'post',
      path: id
        ? `${locationsApiConfig.path}/${location}${apiConfig.path}/${id}`
        : `${locationsApiConfig.path}/${location}${apiConfig.path}`,
      handle: r => r.send(
        id
          ? {
              type: 'box',
              id,
              attributes: {
                identifier: data?.identifier,
                name: data?.name,
                state: 'published'
              }
            }
          : {
              attributes: {
                identifier: data?.identifier,
                name: data?.name,
                state: 'published'
              }
            }
      )
    })
      .then(resp => {
        dispatch(saveBoxByIdSuccess(resp, id));
        done();
      })
      .catch(err => {
        dispatch(saveBoxByIdError(err));
        done();
      });
  }
});

const saveBoxByIdSuccessLogic = createLogicWithApi({
  type: SAVE_BOX_BY_ID_SUCCESS,
  process: ({ getState, action }, dispatch, done) => {
    const state = getState(),
      id = action.payload.data.id,
      locationId = state.table.currentLocation || state.router.location.pathname.split('/')[2] || null,
      baseUrl = window.location.pathname.split('/')[1];

    dispatch(push(`/${baseUrl}/${locationId}/${id}${window.location.search}`));
    done();
  }
});

const removeBoxByIdLogic = createLogicWithApi({
  type: REMOVE_BOX_BY_ID,
  cancelType: CANCEL_REMOVE_BOX_BY_ID,
  latest: true,
  process: ({ Api, getState, action }, dispatch, done) => {
    const state = getState(),
      location = state.table.currentLocation || state.router.location.pathname.split('/')[2] || null;

    return Api({
      method: 'delete',
      path: `${locationsApiConfig.path}/${location}${apiConfig.path}/${action.id}`
    })
      .then(() => {
        dispatch(removeBoxByIdSuccess({ id: action.id }));
        done();
      })
      .catch(err => {
        dispatch(removeBoxByIdError(err));
        done();
      });
  }
});

const removeBoxByIdSuccessLogic = createLogicWithApi({
  type: REMOVE_BOX_BY_ID_SUCCESS,
  process: ({ getState }, dispatch, done) => {
    const state = getState(),
      locationId = state.table.currentLocation || state.router.location.pathname.split('/')[2] || null,
      baseUrl = window.location.pathname.split('/')[1];

    dispatch(push(`/${baseUrl}/${locationId}${window.location.search}`));
    done();
  }
});

// Compartment logic

const fetchCompartmentsLogic = createLogicWithApi({
  type: FETCH_COMPARTMENTS,
  cancelType: CANCEL_FETCH_COMPARTMENTS,
  latest: true,
  process: ({ Api, getState, action }, dispatch, done) => {
    const state = getState(),
      boxId = action.boxId || state.boxes?.selected?.id;

    if (!boxId) {
      done();
      return;
    }

    return Api({
      method: 'get',
      path: `${apiConfig.path}/${boxId}${compartmentsApiConfig.path}`,
      handle: r => {
        const state = getState();
        const sort = action.sort ? action.sort : state.boxes?.compartments?.meta?.sort;
        const desc = typeof action.desc !== 'undefined' ? action.desc : state.boxes?.compartments?.meta?.desc;
        const limit = typeof action.limit !== 'undefined' ? action.limit : 20;
        const offset = typeof action.offset !== 'undefined' ? action.offset : 0;

        if (offset) {
          r.query({ 'offset': offset });
        }

        r
          .query({ 'limit': limit })
          .query({ 'order[field]': sort })
          .query({ 'order[dir]': desc ? 'desc' : 'asc' })
          .query({ 'filters[state]': 'published,temporary' });

        return r;
      }
    })
      .then(resp => {
        dispatch(fetchCompartmentsSuccess(resp));
        done();
      })
      .catch(err => {
        dispatch(fetchCompartmentsError(err));
        done();
      });
  }
});

const fetchCompartmentLogic = createLogicWithApi({
  type: FETCH_COMPARTMENT,
  cancelType: CANCEL_FETCH_COMPARTMENT,
  latest: true,
  process: ({ Api, getState, action }, dispatch, done) => {
    const state = getState(),
      boxId = action.boxId || state.boxes?.selected?.id;

    return Api({
      method: 'get',
      path: `${apiConfig.path}/${boxId}${compartmentsApiConfig.path}/${action.id}`
    })
      .then(resp => {
        dispatch(fetchCompartmentSuccess(resp));
        done();
      })
      .catch(err => {
        dispatch(fetchCompartmentError(err));
        done();
      });
  }
});

const saveCompartmentLogic = createLogicWithApi({
  type: SAVE_COMPARTMENT,
  cancelType: CANCEL_SAVE_COMPARTMENT,
  latest: true,
  process: ({ Api, getState, action }, dispatch, done) => {
    const state = getState(),
      boxId = action.payload.box || state.boxes?.selected?.id,
      data = action.payload,
      id = data.id;

    return Api({
      method: id ? 'patch' : 'post',
      path: id
        ? `${apiConfig.path}/${boxId}${compartmentsApiConfig.path}/${id}`
        : `${apiConfig.path}/${boxId}${compartmentsApiConfig.path}`,
      handle: r => r.send(
        id
          ? {
              type: 'compartment',
              id,
              attributes: {
                identifier: data.identifier,
                name: data.name,
                box: boxId,
                state: 'published'
              }
            }
          : {
              attributes: {
                identifier: data.identifier,
                name: data.name,
                box: boxId,
                state: 'published'
              }
            }
      )
    })
      .then(resp => {
        dispatch(saveCompartmentSuccess(resp));
        done();
      })
      .catch(err => {
        dispatch(saveCompartmentError(err));
        done();
      });
  }
});

const saveCompartmentSuccessLogic = createLogicWithApi({
  type: SAVE_COMPARTMENT_SUCCESS,
  process: ({ getState, action }, dispatch, done) => {
    const state = getState(),
      compartmentId = action.payload.data.id,
      boxId = action.payload.data.attributes.box || state.boxes?.selected?.id,
      locationId = state.table.currentLocation || state.router.location.pathname.split('/')[2] || null,
      baseUrl = window.location.pathname.split('/')[1];

    dispatch(push(`/${baseUrl}/${locationId}/${boxId}/${compartmentId}${window.location.search}`));
    done();
  }
});

const removeCompartmentLogic = createLogicWithApi({
  type: REMOVE_COMPARTMENT,
  cancelType: CANCEL_REMOVE_COMPARTMENT,
  latest: true,
  process: ({ Api, getState, action }, dispatch, done) => {
    const state = getState(),
      boxId = action.boxId || state.boxes?.selected?.id;

    return Api({
      method: 'delete',
      path: `${apiConfig.path}/${boxId}${compartmentsApiConfig.path}/${action.id}`
    })
      .then(() => {
        dispatch(removeCompartmentSuccess({ id: action.id, boxId }));
        done();
      })
      .catch(err => {
        dispatch(removeCompartmentError(err));
        done();
      });
  }
});

const removeCompartmentSuccessLogic = createLogicWithApi({
  type: REMOVE_COMPARTMENT_SUCCESS,
  process: ({ getState, action }, dispatch, done) => {
    const state = getState(),
      boxId = action.boxId || state.boxes?.selected?.id,
      locationId = state.table.currentLocation || state.router.location.pathname.split('/')[2] || null,
      baseUrl = window.location.pathname.split('/')[1];

    dispatch(push(`/${baseUrl}/${locationId}/${boxId}${window.location.search}`));
    done();
  }
});

const boxLogic = [
  checkBoxRouteLogic,
  fetchBoxesLogic,
  fetchBoxByIdLogic,
  saveBoxByIdLogic,
  saveBoxByIdSuccessLogic,
  removeBoxByIdLogic,
  removeBoxByIdSuccessLogic,
  fetchCompartmentsLogic,
  fetchCompartmentLogic,
  saveCompartmentLogic,
  saveCompartmentSuccessLogic,
  removeCompartmentLogic,
  removeCompartmentSuccessLogic
];

export default boxLogic;
